import {
  WBBox,
  WBCheckbox,
  WBFlex,
  WBLinkButton,
  WBTypography,
} from '@admiin-com/ds-web';
import AddNewButton from './AddNewButton';
import { AddRecipientSignModal } from './AddRecipientSignModal';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import DragDropField from './DragDropField';
import { truncateText } from '../../helpers/string';
import { Grid, useTheme, useMediaQuery } from '@mui/material';
import { Signer } from '../../features/providers/TaskCreationProvider/TaskCreationProvider';
import { useTaskCreationContext } from '../../pages/TaskCreation';
import { EntityUser, EntityUserRole } from '@admiin-com/ds-graphql';
import { useLookupService } from '../AutoCompleteLookup/useLookupService';

export const SIGNATURE_COLORS = [
  { iconColour: '#E5CA4C', backgroundColor: '#F7EDC4' },
  { iconColour: '#9DDC8B', backgroundColor: '#D7F7CE' },
  { iconColour: '#8FCFE7', backgroundColor: '#C4E9F7' },
  { iconColour: '#CEA3FC', backgroundColor: '#DDC4F7' },
  { iconColour: '#FFB48F', backgroundColor: '#F7CEBA' },
];

export const RecipientSignature = (props: { onAddClick?: () => void }) => {
  const { t } = useTranslation();
  const [openAddRecipientModal, setOpenAddRecipientModal] =
    React.useState(false);
  const { control, setValue } = useFormContext();
  const signers = useWatch({ control, name: 'signers' });
  const to = useWatch({ control, name: 'to' });
  const { client, isTaxBinding } = useTaskCreationContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const signerLookupEntityId = isTaxBinding
    ? to?.entityId ?? to?.id ?? undefined
    : client?.id ?? undefined;
  const { lookup: fetchSigners } = useLookupService({
    type: 'Signer',
    entityId: signerLookupEntityId,
  });

  const [ownerEntityUser, setOwnerEntityUser] = useState<EntityUser | null>(
    null
  );

  useEffect(() => {
    let active = true;
    if (!fetchSigners || !signerLookupEntityId) {
      if (active) {
        setOwnerEntityUser(null);
      }
      return;
    }

    const loadOwner = async () => {
      const results = await fetchSigners('');
      const owner = results?.find(
        (item: any) =>
          item.searchType === 'ENTITY_USER' &&
          item.role === EntityUserRole.OWNER
      );
      if (active) setOwnerEntityUser(owner ?? null);
    };

    loadOwner();

    return () => {
      active = false;
    };
  }, [fetchSigners, signerLookupEntityId]);

  const isOwnerAdded = useMemo(
    () => signers.some((s: any) => s.id === ownerEntityUser?.userId),
    [signers, ownerEntityUser]
  );

  const handleAddOwner = () => {
    if (!ownerEntityUser || isOwnerAdded || signers.length >= 5) return;
    setValue('signers', [
      ...signers,
      {
        email: ownerEntityUser.email,
        name: `${ownerEntityUser.firstName ?? ''} ${
          ownerEntityUser.lastName ?? ''
        }`.trim(),
        signerType: 'ENTITY_USER',
        id: ownerEntityUser.userId,
        entityId: ownerEntityUser.entityId,
      },
    ]);
  };

  useEffect(() => {
    if (
      signers.length === 0 &&
      ownerEntityUser &&
      ownerEntityUser.email &&
      ownerEntityUser.firstName
    ) {
      setValue('signers', [
        {
          email: ownerEntityUser.email,
          name: `${ownerEntityUser.firstName ?? ''} ${
            ownerEntityUser.lastName ?? ''
          }`.trim(),
          signerType: 'ENTITY_USER',
          id: ownerEntityUser.userId,
          entityId: ownerEntityUser.entityId,
        },
      ]);
    }
  }, [signers.length, ownerEntityUser, setValue]);

  const validSigners = signers.filter(
    (signer: any) => signer.name && signer.email
  );

  const horizontalPadding = 4;
  const verticalPadding = isMobile ? 2 : 3;

  return (
    <WBFlex
      flexDirection="column"
      gap={0.5}
      padding={verticalPadding}
      paddingX={horizontalPadding}
    >
      <WBTypography fontWeight={'bold'} color={'text.secondary'}>
        {t('getAnyoneToSign', { ns: 'taskbox' })}
      </WBTypography>
      <WBTypography mb={2} color={'text.secondary'} variant="body2">
        {t('getAnyoneToSignDescription', { ns: 'taskbox' })}
      </WBTypography>
      {validSigners.map((signer: any, index: number) => (
        <WBBox key={index}>
          <WBTypography color={'text.secondary'} mb={0.5} fontWeight={'medium'}>
            {truncateText(signer.name, 50)}
          </WBTypography>
          {signer.email && (
            <WBTypography variant="caption" color="text.disabled" mb={1}>
              {signer.email}
            </WBTypography>
          )}
          <Grid container spacing={1}>
            <Grid item xs={12} lg={12}>
              <DragDropField
                type="SIGNATURE"
                name={'Sign'}
                signer={{
                  ...signer,
                  color: SIGNATURE_COLORS[index].backgroundColor,
                }}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <DragDropField
                type="DATE"
                signer={{
                  ...signer,
                  color: SIGNATURE_COLORS[index].backgroundColor,
                }}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <DragDropField
                type="NAME"
                signer={{
                  ...signer,
                  color: SIGNATURE_COLORS[index].backgroundColor,
                }}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <DragDropField
                type="INITIAL"
                signer={{
                  ...signer,
                  color: SIGNATURE_COLORS[index].backgroundColor,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name={`signers.${index}.initialPages`}
                render={({ field }) => (
                  <WBCheckbox
                    {...field}
                    checked={field.value ?? false}
                    onChange={(event) => field.onChange(event.target.checked)}
                    label={t('initialPages', { ns: 'taskbox' })}
                  />
                )}
              />
            </Grid>
          </Grid>
        </WBBox>
      ))}
      <AddNewButton
        name={t('addRecipientSignature', { ns: 'taskbox' })}
        onClick={() => {
          if (props.onAddClick) props.onAddClick();
          else setOpenAddRecipientModal(true);
        }}
      />
      {ownerEntityUser && !isOwnerAdded && signers.length < 5 && (
        <WBFlex justifyContent="end" mt={1} color="primary.main">
          <WBLinkButton onClick={handleAddOwner}>
            {t('addOwnerAsSigner', {
              firstName: ownerEntityUser.firstName,
              lastName: ownerEntityUser.lastName,
              ns: 'taskbox',
            })}
          </WBLinkButton>
        </WBFlex>
      )}
      <AddRecipientSignModal
        open={openAddRecipientModal}
        signers={validSigners}
        onClose={() => setOpenAddRecipientModal(false)}
        onDone={(signers: Signer[]) => {
          setValue('signers', [...signers]);
        }}
      />
    </WBFlex>
  );
};
